import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../../api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Wand2, Download, BadgeCheck, ShieldCheck } from "lucide-react";

const findMeta = (list, key) => (list || []).find((s) => s.key === key) || { label: key, color: "#94A3B8" };

export default function CertificationDialog({ module, meta, onClose, onSaved }) {
  const [cert, setCert] = useState(module.certification || {});
  const [reports, setReports] = useState([]);
  const [selected, setSelected] = useState(module.certification?.test_reports || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setCert(module.certification || {}); setSelected(module.certification?.test_reports || []); }, [module]);
  useEffect(() => { api.get("/release/test-reports").then((r) => setReports(r.data)).catch(() => {}); }, []);

  const set = (k, v) => setCert((c) => ({ ...c, [k]: v }));
  const toggleReport = (f) => setSelected((s) => s.includes(f) ? s.filter((x) => x !== f) : [...s, f]);

  const autofill = async () => {
    if (selected.length === 0) return toast.error("Seleziona almeno un report di test");
    try {
      const r = await api.post(`/release/modules/${module.id}/certification/autofill`, { report_files: selected });
      setCert(r.data);
      toast.success("Scheda compilata automaticamente dai report di test");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore autofill"); }
  };

  const save = async (approve = false) => {
    try {
      setSaving(true);
      const payload = {
        version: cert.version, verified_by: cert.verified_by, tests_run: Number(cert.tests_run || 0),
        bugs_fixed: Number(cert.bugs_fixed || 0), bugs_open: Number(cert.bugs_open || 0),
        ready_for_deploy: !!cert.ready_for_deploy, release_notes: cert.release_notes,
        status: approve ? "approvato" : (cert.status || "in_test"),
      };
      const r = await api.put(`/release/modules/${module.id}/certification`, payload);
      setCert(r.data);
      toast.success(approve ? "Modulo approvato e certificato ✅" : "Certificazione salvata");
      onSaved && onSaved();
      if (approve) onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); } finally { setSaving(false); }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/release/modules/${module.id}/certificate.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `certificato_${module.key}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Errore generazione PDF"); }
  };

  const cm = findMeta(meta?.cert_statuses, cert.status);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="certification-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck size={18} className="text-indigo-600" /> Certificazione — {module.name}</DialogTitle>
          <DialogDescription>Compila automaticamente dai report di test. L'approvazione finale è manuale (Admin/Amministratore).</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Stato + versione */}
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full px-3 py-1 text-xs font-medium text-white" style={{ backgroundColor: cm.color }} data-testid="cert-status-badge">{cm.label}</span>
            {cert.certified && <span className="flex items-center gap-1 text-xs font-medium text-emerald-600"><BadgeCheck size={14} /> Certificato il {cert.approval_date}</span>}
          </div>

          {/* Report di test */}
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-700">Report di test</div>
              <Button size="sm" variant="outline" className="gap-1.5" onClick={autofill} data-testid="cert-autofill-btn"><Wand2 size={14} /> Compila automaticamente</Button>
            </div>
            <div className="max-h-36 space-y-1 overflow-y-auto">
              {reports.slice(0, 15).map((r) => (
                <label key={r.file} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-xs hover:bg-slate-50">
                  <Checkbox checked={selected.includes(r.file)} onCheckedChange={() => toggleReport(r.file)} data-testid={`cert-report-${r.iteration}`} />
                  <span className="font-medium text-slate-700">{r.file}</span>
                  <span className="text-slate-400">· {r.tests_run} test · {r.bugs_found} bug</span>
                </label>
              ))}
            </div>
          </div>

          {/* Campi scheda */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div><label className="text-xs text-slate-500">Versione</label><Input value={cert.version || ""} onChange={(e) => set("version", e.target.value)} data-testid="cert-version" /></div>
            <div>
              <label className="text-xs text-slate-500">Stato</label>
              <Select value={cert.status || "in_test"} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="cert-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.cert_statuses || []).map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-xs text-slate-500">Test eseguiti</label><Input type="number" value={cert.tests_run || 0} onChange={(e) => set("tests_run", e.target.value)} data-testid="cert-tests" /></div>
            <div><label className="text-xs text-slate-500">Verificato da</label><Input value={cert.verified_by || ""} onChange={(e) => set("verified_by", e.target.value)} placeholder="Nome verificatore" data-testid="cert-verifiedby" /></div>
            <div><label className="text-xs text-slate-500">Bug risolti</label><Input type="number" value={cert.bugs_fixed || 0} onChange={(e) => set("bugs_fixed", e.target.value)} data-testid="cert-bugs-fixed" /></div>
            <div><label className="text-xs text-slate-500">Bug aperti</label><Input type="number" value={cert.bugs_open || 0} onChange={(e) => set("bugs_open", e.target.value)} data-testid="cert-bugs-open" /></div>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
            <div><div className="text-sm font-medium text-slate-700">Pronto per Deploy</div><div className="text-xs text-slate-400">Nessun bug aperto e verifica completata</div></div>
            <Switch checked={!!cert.ready_for_deploy} onCheckedChange={(v) => set("ready_for_deploy", v)} data-testid="cert-ready-switch" />
          </div>

          <div><label className="text-xs text-slate-500">Note di rilascio</label><Textarea value={cert.release_notes || ""} onChange={(e) => set("release_notes", e.target.value)} placeholder="Cosa include questa versione, changelog, note operative…" data-testid="cert-notes" /></div>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" className="gap-1.5" onClick={downloadPdf} data-testid="cert-pdf-btn"><Download size={14} /> Scarica certificato PDF</Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="cert-save-btn">Salva bozza</Button>
          <Button className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" onClick={() => save(true)} disabled={saving} data-testid="cert-approve-btn"><BadgeCheck size={15} /> Approva & Certifica</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
